import { View, Text, styled, useTheme } from 'tamagui'
import { X, Check } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore, useServerStore } from '@/store'
import { exploreServers } from '@/mocks/explore'

const Backdrop = styled(View, {
  position: 'absolute',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  // @ts-expect-error -- web backdrop-filter
  style: { backdropFilter: 'blur(8px)' },
  zIndex: 2000,
  alignItems: 'center',
  justifyContent: 'center',
})

const Modal = styled(View, {
  width: 440,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  overflow: 'hidden',
})

const ModalHeader = styled(View, {
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
  paddingHorizontal: 20, paddingVertical: 16,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
})

const ModalTitle = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 18, color: '$surfaceForeground',
})

const ModalBody = styled(View, { padding: 20, gap: 16 })

const CloseButton = styled(View, {
  width: 28, height: 28, borderRadius: '$borderRadiusMd', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', hoverStyle: { backgroundColor: '$sidebarHover' },
})

const JoinBtn = styled(View, {
  height: 40, borderRadius: '$borderRadiusLg', alignItems: 'center', justifyContent: 'center',
  backgroundColor: '$accent', cursor: 'pointer',
  hoverStyle: { opacity: 0.9 },
})

const PreviewCard = styled(View, {
  backgroundColor: '$surface', borderRadius: '$borderRadiusLg', borderWidth: 1, borderColor: '$borderDefault',
  padding: 16, flexDirection: 'row', alignItems: 'center', gap: 12,
})

const PreviewIcon = styled(View, {
  width: 48, height: 48, borderRadius: '$borderRadiusXl', alignItems: 'center', justifyContent: 'center',
})

const SuccessView = styled(View, {
  alignItems: 'center', gap: 12, paddingVertical: 16,
})

const SuccessCircle = styled(View, {
  width: 48, height: 48, borderRadius: '$borderRadius3xl', backgroundColor: '$accent',
  alignItems: 'center', justifyContent: 'center',
})

export function JoinServerModal() {
  const { closeJoinServerModal, navigateToServer, setActiveChannel } = useAppStore()
  const { addServer, servers } = useServerStore()
  const theme = useTheme()
  const [code, setCode] = useState('')
  const [joined, setJoined] = useState(false)
  const [error, setError] = useState('')

  // Mock: match invite codes to explore servers
  const findServer = (inviteCode: string) => {
    const normalizedCode = inviteCode.trim().toLowerCase()
    return exploreServers.find(s =>
      s.name.toLowerCase().replace(/\s+/g, '-').includes(normalizedCode) ||
      normalizedCode.includes(s.name.toLowerCase().split(' ')[0])
    )
  }

  const preview = code.length >= 3 ? findServer(code) : null

  const handleJoin = () => {
    if (!code.trim()) {
      setError('Please enter an invite code')
      return
    }

    // Mock: any code of 3+ chars "works"
    const matchedServer = preview || exploreServers[Math.floor(Math.random() * exploreServers.length)]
    if (servers[matchedServer.id]) {
      setError('You already joined this server')
      return
    }

    addServer({
      id: matchedServer.id,
      name: matchedServer.name,
      iconText: matchedServer.iconText,
      iconColor: matchedServer.iconColor,
      categories: [{ id: `${matchedServer.id}-cat0`, name: 'General', collapsed: false }],
      channels: [
        { id: `${matchedServer.id}-ch0`, name: 'general', type: 'text', categoryId: `${matchedServer.id}-cat0`, topic: matchedServer.description, unreadCount: 0, mentionCount: 0 },
        { id: `${matchedServer.id}-ch1`, name: 'Lounge', type: 'voice', categoryId: `${matchedServer.id}-cat0`, unreadCount: 0, mentionCount: 0 },
      ],
      memberCount: matchedServer.memberCount + 1,
      unreadCount: 0,
      mentionCount: 0,
    })

    setJoined(true)

    setTimeout(() => {
      navigateToServer(matchedServer.id)
      setActiveChannel(`${matchedServer.id}-ch0`)
      closeJoinServerModal()
    }, 1500)
  }

  return (
    <Backdrop onPress={closeJoinServerModal}>
      <Modal
        onPress={(e: any) => e.stopPropagation()}
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: theme.shadowModal?.val || 'none' }}
      >
        <ModalHeader>
          <ModalTitle>{joined ? 'Welcome!' : 'Join a Server'}</ModalTitle>
          <CloseButton onPress={closeJoinServerModal}>
            <X size={16} color="$sidebarMuted" />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {joined ? (
            <SuccessView>
              <SuccessCircle>
                <Check size={24} color="$surface" />
              </SuccessCircle>
              <Text fontFamily="$fontBody" fontSize={16} fontWeight="600" color="$surfaceForeground">
                You joined the server!
              </Text>
              <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted">
                Redirecting...
              </Text>
            </SuccessView>
          ) : (
            <>
              <View gap={6}>
                <Text fontFamily="$fontBody" fontSize={12} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={0.5}>
                  Invite Code
                </Text>
                <input
                  value={code}
                  onChange={(e) => { setCode(e.target.value); setError('') }}
                  onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                  placeholder="Enter an invite code or link"
                  style={{
                    background: 'transparent',
                    border: `1px solid ${error ? '#CF3E4F' : (theme.borderDefault?.val || '#2A3142')}`,
                    borderRadius: 6, padding: '10px 14px',
                    color: theme.surfaceForeground?.val || '#E2E8F0',
                    fontFamily: '$fontMono', fontSize: 14,
                    outline: 'none', width: '100%', boxSizing: 'border-box',
                  }}
                />
                {error && (
                  <Text fontFamily="$fontBody" fontSize={12} color="$error">{error}</Text>
                )}
              </View>

              {preview && (
                <PreviewCard>
                  <PreviewIcon backgroundColor={preview.iconColor}>
                    <Text fontFamily="$fontHeading" fontWeight="600" fontSize={16} color="$surface">
                      {preview.iconText}
                    </Text>
                  </PreviewIcon>
                  <View flex={1}>
                    <Text fontFamily="$fontBody" fontWeight="600" fontSize={14} color="$surfaceForeground">
                      {preview.name}
                    </Text>
                    <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">
                      {preview.memberCount.toLocaleString()} members
                    </Text>
                  </View>
                </PreviewCard>
              )}

              <JoinBtn
                onPress={handleJoin}
                // @ts-expect-error -- web box-shadow
                style={{ boxShadow: theme.shadowButton?.val }}
              >
                <Text fontFamily="$fontBody" fontWeight="600" fontSize={14} color="$surface">
                  Join Server
                </Text>
              </JoinBtn>

              <Text fontFamily="$fontBody" fontSize={12} color="$color8" textAlign="center">
                Invites look like: arcus-hq-2026 or a link from a friend
              </Text>
            </>
          )}
        </ModalBody>
      </Modal>
    </Backdrop>
  )
}
